/**
 * @module HypothesisCoLocatedFiles
 * @purpose Renders co-located non-tool files from the context envelope (spatial correlation axis).
 * @depends types/reasoning (CorrelatedEvidence), react-i18next
 * @dependedOnBy HypothesisWalkthrough (step 3)
 *
 * @rules
 * - Performance: pure display — props only, no state, no data fetching
 * - State: none
 * - Errors: n/a — purely presentational; renders empty message when list is empty
 */

import { useTranslation } from 'react-i18next'
import type { CorrelatedEvidence } from '../../../../../types/reasoning'

interface HypothesisCoLocatedFilesProps {
  entries: CorrelatedEvidence[]
}

interface FileCardProps {
  entry: CorrelatedEvidence
}

const CONTAINER_STYLE: React.CSSProperties = {
  padding: '4px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '8px',
}

const FILE_CARD: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  padding: '8px 10px',
  marginBottom: '6px',
}

const BASENAME_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.72rem',
  color: 'var(--text)',
  marginBottom: '4px',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const META_ROW: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.72rem',
  color: 'var(--text-dim)',
  marginTop: '4px',
}

const META_LABEL: React.CSSProperties = {
  color: 'var(--text-muted)',
  marginRight: '4px',
}

const MD5_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-dim)',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
}

function FileCard({ entry }: FileCardProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div style={FILE_CARD}>
      <div style={BASENAME_STYLE}>{entry.basename ?? '—'}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
        {entry.artifact_source !== null && (
          <span style={CHIP_BASE}>{entry.artifact_source}</span>
        )}
        {entry.md5 !== null && (
          <span style={{ ...CHIP_BASE, fontFamily: 'var(--font-mono)' }}>
            <span style={MD5_STYLE}>{t('reasoning.hypothesis.coLocatedFiles.md5Label')}: {entry.md5}</span>
          </span>
        )}
      </div>
      <div style={META_ROW}>
        <span style={META_LABEL}>{t('reasoning.hypothesis.coLocatedFiles.reasonLabel')}:</span>
        {entry.correlation_reason}
      </div>
    </div>
  )
}

/**
 * Renders the co-located files step — spatial-axis correlated evidence items.
 *
 * @behaviour Displays each co-located file as a card with basename (mono), md5 (mono if present),
 *   artifact_source badge, and correlation_reason. Empty state renders a neutral message.
 * @errors n/a
 */
export function HypothesisCoLocatedFiles({ entries }: HypothesisCoLocatedFilesProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const spatialEntries = entries.filter((entry) => entry.correlation_axis === 'spatial')

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.coLocatedFiles.title')}</div>
      {spatialEntries.length === 0
        ? <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.coLocatedFiles.empty')}</div>
        : spatialEntries.map((entry, index) => (
          <FileCard key={entry.observation_id ?? `spatial-${String(index)}`} entry={entry} />
        ))
      }
    </div>
  )
}
